import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import Navigation from "../../Navigation";
import Footer from "../../Footer";
import { Link } from "react-router-dom";

const BinaryStarSystemPage = () => {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);
  const [activeVelocityTab, setActiveVelocityTab] = useState("normal");

  // Initialize section refs
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 2); // 两个部分
  }, []);

  // Handle scrolling
  useEffect(() => {
    const handleScroll = () => {
      // Determine active section based on scroll position
      const currentPosition = window.pageYOffset + 300;
      let active = 0;

      sectionRefs.current.forEach((ref, index) => {
        if (ref && ref.offsetTop <= currentPosition) {
          active = index;
        }
      });

      if (active !== activeSection) {
        setActiveSection(active);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  // Translations
  const translations = {
    backToProjects: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    title: {
      en: "Binary Star System Simulation",
      zh: "双星系统模拟",
      ja: "連星システムシミュレーション",
    },
    tagline: {
      en: "Numerical simulation of binary star system with debris capture dynamics using MATLAB",
      zh: "使用MATLAB进行双星系统与碎片捕获动力学的数值模拟",
      ja: "MATLABを使用したデブリ捕獲ダイナミクスを含む連星システムの数値シミュレーション",
    },
    overview: {
      en: "Project Overview",
      zh: "项目概述",
      ja: "プロジェクト概要",
    },
    description: {
      en: "This simulation models a binary star system and studies debris capture dynamics. We investigate how star velocity affects the amount of debris particles captured by stars as they orbit around their common center of mass.",
      zh: "本模拟对双星系统进行建模并研究碎片捕获动力学。我们探究恒星速度如何影响恒星在围绕共同质心运行时捕获的碎片粒子数量。",
      ja: "このシミュレーションは連星系をモデル化し、デブリ捕獲ダイナミクスを研究します。恒星が共通の質量中心を周回する際、恒星速度がデブリ粒子の捕獲量にどのように影響するかを調査します。",
    },
    findings: {
      en: "Key Findings",
      zh: "主要发现",
      ja: "主な発見",
    },
    findingsText: {
      en: "We found an inverse relationship between star velocity and debris capture. Optimal debris capture occurs at approximately 0.5× standard velocity, with system instability below 0.4× (collision) and above 1.4× (disintegration).",
      zh: "我们发现恒星速度与碎片捕获之间存在反比关系。最佳碎片捕获发生在约0.5倍标准速度，系统在低于0.4倍时不稳定(碰撞)，高于1.4倍时解体。",
      ja: "恒星の速度とデブリ捕獲の間に逆相関関係を発見しました。最適なデブリ捕獲は標準速度の約0.5倍で発生し、0.4倍未満では不安定(衝突)、1.4倍以上では崩壊します。",
    },
    methodology: {
      en: "Methodology",
      zh: "方法论",
      ja: "方法論",
    },
    methodologyText: {
      en: "We employed the Runge-Kutta method to solve differential equations describing stellar motion and debris interaction, ensuring energy and angular momentum conservation throughout the simulation.",
      zh: "我们采用龙格-库塔方法求解描述恒星运动和碎片相互作用的微分方程，确保在整个模拟过程中能量和角动量守恒。",
      ja: "ルンゲ＝クッタ法を用いて恒星運動とデブリ相互作用を記述する微分方程式を解き、シミュレーション全体でエネルギーと角運動量の保存を確保しました。",
    },
    velocityStudy: {
      en: "Velocity Study",
      zh: "速度研究",
      ja: "速度の研究",
    },
    velocityExplain: {
      en: "We tested how different star velocities affect debris capture rates:",
      zh: "我们测试了不同的恒星速度如何影响碎片捕获率：",
      ja: "異なる恒星速度がデブリ捕獲率にどう影響するかをテストしました：",
    },
    lowVelocity: {
      en: "Low Velocity (0.5×)",
      zh: "低速度 (0.5×)",
      ja: "低速 (0.5×)",
    },
    normalVelocity: {
      en: "Normal Velocity (1.0×)",
      zh: "正常速度 (1.0×)",
      ja: "通常速度 (1.0×)",
    },
    highVelocity: {
      en: "High Velocity (1.2×)",
      zh: "高速度 (1.2×)",
      ja: "高速 (1.2×)",
    },
    lowVelocityResult: {
      en: "Maximum debris capture (86 particles)",
      zh: "最大碎片捕获（86个粒子）",
      ja: "最大デブリ捕獲（86粒子）",
    },
    normalVelocityResult: {
      en: "Moderate debris capture (47 particles)",
      zh: "中等碎片捕获（47个粒子）",
      ja: "中程度のデブリ捕獲（47粒子）",
    },
    highVelocityResult: {
      en: "Minimum debris capture (36 particles)",
      zh: "最小碎片捕获（36个粒子）",
      ja: "最小デブリ捕獲（36粒子）",
    },
    thresholds: {
      en: "Stability Thresholds",
      zh: "稳定性阈值",
      ja: "安定性の閾値",
    },
    lowThreshold: {
      en: "Below 0.4× standard velocity: Stars collide due to insufficient centripetal force",
      zh: "低于0.4倍标准速度：由于向心力不足，恒星相撞",
      ja: "標準速度の0.4倍未満：向心力不足により恒星が衝突",
    },
    highThreshold: {
      en: "Above 1.4× standard velocity: System disintegrates as stars escape mutual gravity",
      zh: "高于1.4倍标准速度：恒星逃脱互相引力，系统解体",
      ja: "標準速度の1.4倍以上：恒星が相互の重力から逃れ、システムが崩壊",
    },
    courseInfo: {
      en: "Course Information",
      zh: "课程信息",
      ja: "コース情報",
    },
    course: {
      en: "Course:",
      zh: "课程:",
      ja: "コース:",
    },
    instructor: {
      en: "Instructor:",
      zh: "教师:",
      ja: "教授:",
    },
    differentiableManifold: {
      en: "Differentiable Manifold",
      zh: "可微流形",
      ja: "微分可能な多様体",
    },
    githubLink: {
      en: "View on GitHub",
      zh: "在GitHub上查看",
      ja: "GitHubで表示",
    },
  };

  // Technical stack data
  const techStack = [
    { name: "MATLAB", category: "language", color: "blue" },
    { name: "Numerical Methods", category: "concept", color: "purple" },
    { name: "Physics Simulation", category: "concept", color: "purple" },
    { name: "Gravitational Dynamics", category: "concept", color: "purple" },
    { name: "Runge-Kutta Method", category: "algorithm", color: "green" },
    { name: "3D Visualization", category: "feature", color: "teal" },
  ];

  // Get tag color by category
  const getTagColor = (category) => {
    switch (category) {
      case "language":
        return "bg-blue-100 text-blue-800";
      case "algorithm":
        return "bg-green-100 text-green-800";
      case "concept":
        return "bg-purple-100 text-purple-800";
      case "feature":
        return "bg-teal-100 text-teal-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50">
      <Navigation activeSection="projects" />
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-6 flex justify-between items-center">
            <Link
              to="/projects"
              className="text-primary-500 hover:text-primary-600 transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {translations.backToProjects[language] ||
                translations.backToProjects.en}
            </Link>

            {/* GitHub链接 */}
            <a
              href="https://github.com/markjailenechan/binary-star-system-simulation"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="mr-2"
              >
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              {translations.githubLink[language] || translations.githubLink.en}
            </a>
          </div>

          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-light mb-4 text-neutral-800">
              {translations.title[language] || translations.title.en}
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              {translations.tagline[language] || translations.tagline.en}
            </p>

            {/* Technology stack tags */}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {techStack.map((tech, index) => (
                <span
                  key={index}
                  className={`${getTagColor(
                    tech.category
                  )} px-3 py-1 rounded-full text-sm font-medium`}
                >
                  {tech.name}
                </span>
              ))}
            </div>
          </header>

          {/* Project overview */}
          <section
            ref={(el) => (sectionRefs.current[0] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.overview[language] || translations.overview.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.description[language] ||
                translations.description.en}
            </p>

            {/* Project showcase image */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div className="p-2">
                <img
                  src="/assets/projects/binaryStarSystem/cover.gif"
                  alt="Binary Star System Simulation"
                  className="w-full rounded-lg"
                />
                <div className="text-center text-sm text-neutral-500 mt-2">
                  {language === "en"
                    ? "3D visualization of binary stars with debris field"
                    : language === "zh"
                    ? "双星系统与碎片场的3D可视化"
                    : "連星とデブリフィールドの3D可視化"}
                </div>
              </div>
            </div>

            {/* Course information */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-medium mb-4 text-neutral-800">
                {translations.courseInfo[language] ||
                  translations.courseInfo.en}
              </h3>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.course[language] || translations.course.en}
                </span>{" "}
                CSCI-UA MATH-UA 144 330 2024 Spring 001 (Undergrad)
              </p>
              <p className="text-neutral-700 mb-2">
                <span className="font-medium">
                  {translations.instructor[language] ||
                    translations.instructor.en}
                </span>{" "}
                Prof. Charles S. Peskin
              </p>
            </div>
          </section>

          {/* Key findings */}
          <section
            ref={(el) => (sectionRefs.current[1] = el)}
            className="mb-16"
          >
            <h2 className="text-2xl font-light mb-6 text-neutral-800 border-b border-neutral-200 pb-2">
              {translations.findings[language] || translations.findings.en}
            </h2>
            <p className="text-neutral-700 mb-8 leading-relaxed">
              {translations.findingsText[language] ||
                translations.findingsText.en}
            </p>

            {/* Velocity study with tabs */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div className="p-6">
                <h3 className="text-lg font-medium mb-4 text-neutral-800">
                  {translations.velocityStudy[language] ||
                    translations.velocityStudy.en}
                </h3>
                <p className="text-neutral-700 mb-4">
                  {translations.velocityExplain[language] ||
                    translations.velocityExplain.en}
                </p>

                {/* Velocity tabs */}
                <div className="mb-6 border-b border-gray-200">
                  <div className="flex flex-wrap -mb-px">
                    <button
                      className={`inline-block p-4 rounded-t-lg ${
                        activeVelocityTab === "low"
                          ? "border-b-2 border-blue-600 text-blue-600"
                          : "border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300"
                      }`}
                      onClick={() => setActiveVelocityTab("low")}
                    >
                      {translations.lowVelocity[language] ||
                        translations.lowVelocity.en}
                    </button>
                    <button
                      className={`inline-block p-4 rounded-t-lg ${
                        activeVelocityTab === "normal"
                          ? "border-b-2 border-blue-600 text-blue-600"
                          : "border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300"
                      }`}
                      onClick={() => setActiveVelocityTab("normal")}
                    >
                      {translations.normalVelocity[language] ||
                        translations.normalVelocity.en}
                    </button>
                    <button
                      className={`inline-block p-4 rounded-t-lg ${
                        activeVelocityTab === "high"
                          ? "border-b-2 border-blue-600 text-blue-600"
                          : "border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300"
                      }`}
                      onClick={() => setActiveVelocityTab("high")}
                    >
                      {translations.highVelocity[language] ||
                        translations.highVelocity.en}
                    </button>
                  </div>
                </div>

                {/* Velocity content */}
                <div>
                  {activeVelocityTab === "low" && (
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-1/2 p-4 flex flex-col justify-center">
                        <p className="font-medium text-neutral-800 mb-2 text-left">
                          {translations.lowVelocityResult[language] ||
                            translations.lowVelocityResult.en}
                        </p>
                        <ul className="list-disc pl-5 text-neutral-700 text-left">
                          <li className="mb-2">
                            {language === "en"
                              ? "Stars move in highly elliptical, overlapping orbits"
                              : language === "zh"
                              ? "恒星在高度椭圆形的重叠轨道中运动"
                              : "恒星は高度に楕円形で重なり合う軌道で移動"}
                          </li>
                          <li className="mb-2">
                            {language === "en"
                              ? "Longer interaction time with debris field"
                              : language === "zh"
                              ? "与碎片场的相互作用时间更长"
                              : "デブリフィールドとの相互作用時間が長い"}
                          </li>
                          <li className="mb-2">
                            {language === "en"
                              ? "Low velocity simulation"
                              : language === "zh"
                              ? "低速度模拟"
                              : "低速シミュレーション"}
                          </li>
                        </ul>
                      </div>
                      <div className="md:w-1/2 p-4 flex justify-center items-center">
                        <img
                          src="/assets/projects/binaryStarSystem/low_velocity.gif"
                          alt="Low velocity simulation"
                          className="max-w-full h-auto rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  )}

                  {activeVelocityTab === "normal" && (
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-1/2 p-4 flex flex-col justify-center">
                        <p className="font-medium text-neutral-800 mb-2 text-left">
                          {translations.normalVelocityResult[language] ||
                            translations.normalVelocityResult.en}
                        </p>
                        <ul className="list-disc pl-5 text-neutral-700 text-left">
                          <li className="mb-2">
                            {language === "en"
                              ? "Stars move in stable, slightly elliptical orbits"
                              : language === "zh"
                              ? "恒星在稳定的、略微椭圆形的轨道中运动"
                              : "恒星は安定した、わずかに楕円形の軌道で移動"}
                          </li>
                          <li className="mb-2">
                            {language === "en"
                              ? "Balanced gravitational influence and orbital velocity"
                              : language === "zh"
                              ? "平衡的引力影响和轨道速度"
                              : "重力的影響と軌道速度のバランスがとれている"}
                          </li>
                          <li className="mb-2">
                            {language === "en"
                              ? "Normal velocity simulation"
                              : language === "zh"
                              ? "正常速度模拟"
                              : "通常速度シミュレーション"}
                          </li>
                        </ul>
                      </div>
                      <div className="md:w-1/2 p-4 flex justify-center items-center">
                        <img
                          src="/assets/projects/binaryStarSystem/cover.gif"
                          alt="Normal velocity simulation"
                          className="max-w-full h-auto rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  )}

                  {activeVelocityTab === "high" && (
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-1/2 p-4 flex flex-col justify-center">
                        <p className="font-medium text-neutral-800 mb-2 text-left">
                          {translations.highVelocityResult[language] ||
                            translations.highVelocityResult.en}
                        </p>
                        <ul className="list-disc pl-5 text-neutral-700 text-left">
                          <li className="mb-2">
                            {language === "en"
                              ? "Stars move in faster, more circular orbits"
                              : language === "zh"
                              ? "恒星在更快速、更圆形的轨道中运动"
                              : "恒星はより速く、より円形の軌道で移動"}
                          </li>
                          <li className="mb-2">
                            {language === "en"
                              ? "Reduced interaction time with debris field"
                              : language === "zh"
                              ? "与碎片场的相互作用时间减少"
                              : "デブリフィールドとの相互作用時間が短い"}
                          </li>
                          <li className="mb-2">
                            {language === "en"
                              ? "High velocity simulation"
                              : language === "zh"
                              ? "高速度模拟"
                              : "高速シミュレーション"}
                          </li>
                        </ul>
                      </div>
                      <div className="md:w-1/2 p-4 flex justify-center items-center">
                        <img
                          src="/assets/projects/binaryStarSystem/high_velocity.gif"
                          alt="High velocity simulation"
                          className="max-w-full h-auto rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BinaryStarSystemPage;
